<template>
  <form role="form" class="form comment-form" novalidate @submit.prevent="save">
    <div class="form__group">
      <UsernameInput ref="username" :model="model" :validation="validation" />
    </div>
    <div class="form__group">
      <InputWithContextMenu
        ref="input"
        v-model="model.text"
        elementType="textarea"
        rows="2"
        type="text"
        class="form-control mb-4"
        placeholder="Write a comment..."
        :class="validation.stateClass('text')"
      />
      <FormError :errors="validation.errors" field="text" />
    </div>
    <div class="form__group">
      <button
        v-if="!doc.isCommunityEOC"
        v-wait:disabled="'Posting Comment'"
        type="submit"
        class="btns btns--large"
      >
        <v-wait for="Posting Comment">
          <template v-slot:waiting>
            Posting
            <button-spinner />
          </template>
          Post Comment
        </v-wait>
      </button>
      <router-link
        v-else
        :to="{ name: 'billing' }"
        class="btns btns--large eoc-button"
      >
        Subscribe to Post Comment
      </router-link>
    </div>
  </form>
</template>

<script setup lang="ts">
import { ref } from 'vue'

import FormError from '@/components/ui/FormError.vue'
import ButtonSpinner from '@/components/ui/ButtonSpinner.vue'
import InputWithContextMenu from '@/app/components/InputWithContextMenu.vue'

import UsernameInput from './UsernameInput.vue'

import { useWait } from '@/app/composables/useWait'
import { setFocus, ValidationState } from '@/helpers/form'
import { backend } from '@/services/backend'
import type { Thread } from '@/models/interfaces'
import type { DocView } from '@/models/doc'

const EMPTY_COMMENT = { id: null, text: '' }

const props = defineProps<{
  doc: DocView
  thread: Thread
}>()

const wait = useWait()

const username = ref<InstanceType<typeof UsernameInput> | null>(null)
const input = ref<InstanceType<typeof InputWithContextMenu> | null>(null)

const validation = ref(new ValidationState())
const model = ref<any>(Object.assign({}, EMPTY_COMMENT))

async function save(): Promise<void> {
  try {
    wait.start('Posting Comment')
    const comment = await backend.saveComment(
      props.doc,
      props.thread,
      model.value,
    )
    /* eslint-disable vue/no-mutating-props */
    props.thread.comments.unshift(comment)
    props.thread.comments_count += 1
    // Set the "notify_new_comment" toggle for the parent thread
    if (comment.notify_new_comment !== undefined) {
      props.thread.notify_new_comment = comment.notify_new_comment
    }
    /* eslint-enable vue/no-mutating-props */
    validation.value.reset()
    username.value?.saved()
    model.value = Object.assign({}, EMPTY_COMMENT)
  } catch (error) {
    validation.value.showErrors(error)
  } finally {
    wait.end('Posting Comment')
  }
}

function focusInput(): void {
  setFocus(input.value?.$el)
}

defineExpose({
  focusInput,
})
</script>
